import { useCallback, useEffect, useRef, useState } from "react"
import { getPointChartData } from "@lib/api/resorts"
import { message } from "@components/Message/message"

export function getFromTo(i) {
    let res
    if (!i.minDays || !i.maxDays) {
        res = i.minDays || i.maxDays
    } else if (i.minDays === i.maxDays) {
        res = i.minDays
    } else {
        res = `${i.minDays}-${i.maxDays}`
    }
    return (res === 1 ? `${res} night` : `${res} nights`) + " / year"
}

export const titleMap = {
    "DELUXE STUDIO": "Studio",
    "ONE-BEDROOM VILLA": "1 Bedroom",
    "TWO-BEDROOM VILLA": "2 Bedroom",
    "THREE-BEDROOM GRAND VILLA": "3 Bedroom"
}
const daysProgress = 10
const calculate = (selectedChart, points, period) => {
    return selectedChart.headers
        .map((header) => {
            const columnsAvailable = [1, 2, 3, 4, 5].filter((i) => header["points_" + i])
            let minDays
            let maxDays
            selectedChart.rows
                .filter((i) => i.period === period)
                .forEach((row) => {
                    if (row.nights === "WEEKLY") {
                        return
                    }
                    row.cells.forEach((cell) => {
                        if (cell.column === header.column) {
                            columnsAvailable.forEach((coll) => {
                                const key = "points_" + coll
                                const value = cell[key]
                                const days = points / value
                                if (days > 0) {
                                    if (days > 1) {
                                        minDays = minDays ? Math.min(days, minDays) : days
                                    }
                                    maxDays = maxDays ? Math.max(days, maxDays) : days
                                }
                            })
                        }
                    })
                })
            minDays = minDays ? Math.floor(minDays) : 0
            maxDays = maxDays ? Math.floor(maxDays) : 0
            const days = (minDays || 0) + (maxDays || 0) / 2
            return {
                minDays,
                maxDays,
                title: header.title,
                progress: Math.max(Math.min(days / daysProgress, 1), 0) * 10 * 10
            }
        })
        .sort((a, b) => {
            const aDays = (a.minDays || 0) + (a.maxDays || 0)
            const bDays = (b.minDays || 0) + (b.maxDays || 0)
            return bDays - aDays
        })
}

const showError = () => {
    message.warning("No point chart data found for current resort ")
}

async function getData({ resortId, signal }) {
    return getPointChartData(resortId, signal)
}

export const usePointChart = (resort, points, chartData) => {
    const [data, setData] = useState(() => {
        let _data = {}
        if (chartData) {
            const allCharts = chartData
            const currentYear = new Date().getFullYear()
            const charts = allCharts.filter((i) => i.year >= currentYear)
            const selectedChart = charts.find((i) => i.year === currentYear) || charts[0] || allCharts[0]
            if (selectedChart) {
                const years = charts.map((i) => i.year)
                const rows = calculate(selectedChart, points, 1)
                _data = {
                    loading: false,
                    selectedChart,
                    allCharts: charts,
                    years: years.length ? years : [selectedChart.year],
                    year: selectedChart.year,
                    periods: selectedChart.periods,
                    rows
                }
            }
        }
        return {
            popoverOpen: false,
            loading: true,
            years: [],
            resortId: resort.id,
            points,
            year: null,
            allCharts: [],
            periods: [],
            selectedChart: null,
            period: 1,
            rows: [],
            ..._data
        }
    })
    /**
     * @type {React.MutableRefObject<AbortController>}
     */
    const signalRef = useRef(null)
    useEffect(() => {
        if (!signalRef.current?.signal.aborted) {
            signalRef.current?.abort("close old request")
        }

        signalRef.current = new AbortController()

        getData({ resortId: data.resortId, signal: signalRef.current.signal }).then((allCharts) => {
            if (allCharts) {
                const currentYear = new Date().getFullYear()
                const charts = allCharts.filter((i) => i.year >= currentYear)
                const selectedChart = charts.find((i) => i.year === currentYear) || charts[0] || allCharts[0]
                if (!selectedChart) {
                    showError()
                    return
                }
                const years = charts.map((i) => i.year)
                const rows = calculate(selectedChart, data.points, data.period)
                setData({
                    ...data,
                    selectedChart,
                    allCharts: charts,
                    years: years.length ? years : [selectedChart.year],
                    year: selectedChart.year,
                    loading: false,
                    periods: selectedChart.periods,
                    rows
                })
            } else {
                showError()
            }
        })
    }, [data.resortId])
    return {
        ...data,
        onChange: (period) => {
            if (period === data.period) return
            setData({
                ...data,
                period,
                popoverOpen: false,
                rows: calculate(data.selectedChart, data.points, period)
            })
        },
        togglePeriodsPopup: useCallback((popoverOpen) => {
            setData((data) => ({
                ...data,
                popoverOpen
            }))
        }, []),
        changePoints: (points) => {
            if (data.points === points) return
            setData({
                ...data,
                points,
                rows: calculate(data.selectedChart, points, data.period)
            })
        },
        changeYear: (yearString) => {
            const year = Number(yearString)
            if (year === data.year) return
            const selectedChart = data.allCharts.find((i) => i.year === year)
            setData({
                ...data,
                year,
                selectedChart,
                periods: selectedChart.periods,
                rows: calculate(selectedChart, data.points, data.period)
            })
        },
        onChangeResort: (id) => {
            if (id === data.resortId) return
            setData({
                ...data,
                loading: true,
                resortId: id
            })
        }
    }
}
